export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  SUCCESS = 'success',
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
  source?: string;
  requestId?: string;
}

export interface ApiLogEntry extends LogEntry {
  method?: string;
  url?: string;
  status?: number;
  duration?: number;
  requestBody?: any;
  responseBody?: any;
  headers?: Record<string, string>;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private isEnabled = true;

  private addLog(entry: LogEntry): void {
    if (!this.isEnabled) return;

    try {
      // Add to internal logs
      this.logs.push(entry);

      // Keep only the last maxLogs entries
      if (this.logs.length > this.maxLogs) {
        this.logs = this.logs.slice(-this.maxLogs);
      }

      // Console logging for development
      if (typeof window !== 'undefined' && import.meta.env.NODE_ENV === 'development') {
        const timestamp = new Date().toISOString();
        const sourcePrefix = entry.source ? `[${entry.source}]` : '';
        const message = `${timestamp} ${entry.level.toUpperCase()} ${sourcePrefix} ${entry.message}`;

        switch (entry.level) {
          case LogLevel.ERROR:
            console.error(message, entry.data);
            break;
          case LogLevel.WARN:
            console.warn(message, entry.data);
            break;
          case LogLevel.DEBUG:
            console.debug(message, entry.data);
            break;
          case LogLevel.SUCCESS:
            console.log(`%c${message}`, 'color: green; font-weight: bold;', entry.data);
            break;
          default:
            console.log(message, entry.data);
        }
      }
    } catch (error) {
      // Silent fallback to prevent logger from breaking the app
      console.error('Logger error:', error);
    }
  }

  debug(message: string, data?: any, source?: string): void {
    this.addLog({
      timestamp: new Date().toISOString(),
      level: LogLevel.DEBUG,
      message,
      data,
      source,
    });
  }

  info(message: string, data?: any, source?: string): void {
    this.addLog({
      timestamp: new Date().toISOString(),
      level: LogLevel.INFO,
      message,
      data,
      source,
    });
  }

  warn(message: string, data?: any, source?: string): void {
    this.addLog({
      timestamp: new Date().toISOString(),
      level: LogLevel.WARN,
      message,
      data,
      source,
    });
  }

  error(message: string, data?: any, source?: string): void {
    this.addLog({
      timestamp: new Date().toISOString(),
      level: LogLevel.ERROR,
      message,
      data,
      source,
    });
  }

  success(message: string, data?: any, source?: string): void {
    this.addLog({
      timestamp: new Date().toISOString(),
      level: LogLevel.SUCCESS,
      message,
      data,
      source,
    });
  }
  // API-specific logging methods
  logApiRequest(method: string, url: string, headers?: Record<string, string>, body?: any, requestId?: string): void {
    this.addLog({
      timestamp: new Date().toISOString(),
      level: LogLevel.INFO,
      message: `API Request: ${method} ${url}`,
      data: {
        method,
        url,
        headers: this.sanitizeHeaders(headers),
        body: this.sanitizeBody(body),
      },
      source: 'API',
      requestId,
    } as ApiLogEntry);
  }

  logApiResponse(method: string, url: string, status: number, duration: number, headers?: Record<string, string>, body?: any, requestId?: string): void {
    const level = status >= 400 ? LogLevel.ERROR : LogLevel.SUCCESS;
    this.addLog({
      timestamp: new Date().toISOString(),
      level,
      message: `API Response: ${method} ${url} - ${status} (${duration}ms)`,
      data: {
        method,
        url,
        status,
        duration,
        headers: this.sanitizeHeaders(headers),
        body: this.sanitizeBody(body),
      },
      source: 'API',
      requestId,
    } as ApiLogEntry);
  }

  logApiError(method: string, url: string, error: any, duration?: number, requestId?: string): void {
    this.addLog({
      timestamp: new Date().toISOString(),
      level: LogLevel.ERROR,
      message: `API Error: ${method} ${url} - ${error?.message || error}`,
      data: {
        method,
        url,
        error: error?.message || error,
        stack: error?.stack,
        duration,
      },
      source: 'API',
      requestId,
    } as ApiLogEntry);
  }

  // Simple sanitization (case-insensitive match on header keys)
  private sanitizeHeaders(headers?: Record<string, string>): Record<string, string> | undefined {
    if (!headers) return headers;

    const sensitiveKeys = ['authorization', 'cookie', 'x-api-key', 'token'];
    const sanitized: Record<string, string> = {};

    Object.entries(headers).forEach(([key, value]) => {
      const keyLower = key.toLowerCase();
      sanitized[key] = sensitiveKeys.some(sk => keyLower === sk || keyLower.includes(sk))
        ? '[REDACTED]'
        : value;
    });

    return sanitized;
  }

  private sanitizeBody(body?: any): any {
    if (!body) return body;

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return this.sanitizeObject(parsed);
      } catch {
        return body;
      }
    }

    return this.sanitizeObject(body);
  }

  private sanitizeObject(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;

    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'authorization'];
    const sanitized = { ...obj };

    Object.keys(sanitized).forEach(key => {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object') {
        sanitized[key] = this.sanitizeObject(sanitized[key]);
      }
    });

    return sanitized;
  }

  // Get logs for debugging
  getLogs(level?: LogLevel, source?: string): LogEntry[] {
    let filtered = this.logs;

    if (level) {
      filtered = filtered.filter(log => log.level === level);
    }

    if (source) {
      filtered = filtered.filter(log => log.source === source);
    }

    return filtered;
  }

  // Get API logs specifically
  getApiLogs(): ApiLogEntry[] {
    return this.logs.filter(log => log.source === 'API') as ApiLogEntry[];
  }

  // Clear logs
  clearLogs(): void {
    this.logs = [];
  }

  // Enable/disable logging
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  // Export logs for debugging
  exportLogs(): string {
    try {
      return JSON.stringify(this.logs, null, 2);
    } catch (error) {
      return JSON.stringify({ error: 'Failed to export logs' });
    }
  }
}

// Create singleton instance
export const logger = new Logger(); 